import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ICommand, ICommandContext, IDockingContext, IDockingInfo } from '../../../model/docking';
import { ILocationContext } from '../../../model/location-context';
import { LogService } from '../logging/log.service';
import { MaintenanceService } from '../maintenance/maintenance.service';
import { IUserPinStore } from '../../../model/maintenance.interface';
import DockingActions from './store/docking.action-types';
import { DockingState } from './store/docking.reducers';
import { dockingContextSelector, userMonitoringSelector } from './store/docking.selectors';
import { IHeartbeat } from '../../../model/heartbeat';
import moment from 'moment';
import { DeviceSource } from '../../../model/license';

@Injectable({
    providedIn: 'root'
})
export class DockingService implements IUserPinStore {
    private readonly SLOW_CONNECTION_THROTTLE_TIME = 2000;
    LOG_SOURCE = 'DockingService';
    subFopConfig: Subscription;
    public userEnteredPin = '';
    lastHeartBeatResponse: IHeartbeat | undefined;
    slowConnectionTimeout: NodeJS.Timer;
    isSlowConnection: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly store: Store<DockingState>,
        private readonly logService: LogService,
        private readonly maintenanceService: MaintenanceService
    ) {}

    requestDockingContext(locationContext: ILocationContext) {
        this.store.dispatch(DockingActions.requestDockingContext({ locationContext }));
        this.logService.info(this.LOG_SOURCE, `Request Docking Context with: `, locationContext);
    }

    loadDockingContext(dockingContext: IDockingContext) {
        this.maintenanceService.setDeviceIdInSession(dockingContext.deviceId);
        this.store.dispatch(DockingActions.loadDockingContext({ dockingContext }));
    }

    getDockingContext(): Observable<IDockingContext> {
        return this.store.select(dockingContextSelector);
    }

    setUserMonitoring(userMonitoring: boolean): void {
        this.store.dispatch(DockingActions.setUserMonitoring({ userMonitoring }));
    }

    getUserMonitoring(): Observable<boolean> {
        return this.store.select(userMonitoringSelector);
    }

    processHeartBeatResponse(heartBeatResponse: IHeartbeat): void {
        if (!this.lastHeartBeatResponse) {
            this.lastHeartBeatResponse = heartBeatResponse;
        }

        const timeDiff = moment(heartBeatResponse.responseTimeString).diff(
            this.lastHeartBeatResponse.responseTimeString
        );

        if (timeDiff > 0) {
            clearTimeout(this.slowConnectionTimeout);
            if (this.isSlowConnection.getValue() && timeDiff < this.SLOW_CONNECTION_THROTTLE_TIME) {
                this.isSlowConnection.next(false);
            }
            this.lastHeartBeatResponse = heartBeatResponse;

            this.slowConnectionTimeout = setTimeout(() => {
                this.isSlowConnection.next(true);
            }, this.SLOW_CONNECTION_THROTTLE_TIME);
        }
    }

    isSlowConnection$(): Observable<boolean> {
        return this.isSlowConnection.asObservable();
    }

    resetSlowConnectionState(): void {
        this.isSlowConnection.next(false);
    }

    triggerDockingCommand(
        locationContext: ILocationContext,
        command: ICommand,
        operator: string,
        isFop: boolean,
        dockingInfo?: IDockingInfo
    ) {
        const commandContext: ICommandContext = {
            locationContext,
            command: { ...command, source: isFop ? DeviceSource.FOP : DeviceSource.MOP },
            dockingInfo
        };

        this.store.dispatch(
            DockingActions.triggerDockingCommand({ commandContext: commandContext, operator: operator })
        );
        this.logService.info(this.LOG_SOURCE, `Executed command '${command?.text}'`, { command });
    }

    triggerBackCommand(locationContext: ILocationContext) {
        this.requestDockingContext(locationContext);
    }

    storeUserPin(pin: string): void {
        this.userEnteredPin = pin;
    }

    clearUserPin(): void {
        this.userEnteredPin = '';
    }
}
